import { useCreateApprovalDomainApprovers } from '@hooks/useApprovalDomainApprovers'
import { useSet } from '@hooks/useSet'
import { UserSelection } from '@features/user-selection'
import { Modal } from 'components/notifications'
import { useState } from 'react'
import * as Sentry from '@sentry/react'
import { ApproverModalContent } from './ApproverModalContent'
import { EntityItem } from '@features/entity-picker'

interface AddApproverModalProps {
  approvalDomainId: string
  onClose: () => void
  onError: () => void
  onSuccess: () => void
  userIdsAlreadyAsApprover: Set<number>
}

export const AddApproverModal = ({
  approvalDomainId,
  onClose,
  onError,
  onSuccess,
  userIdsAlreadyAsApprover,
}: AddApproverModalProps) => {
  const {
    set: selectedFacilityIds,
    add: selectFacility,
    remove: unselectFacility,
    clear: clearSelectedFacilityIds,
  } = useSet<number>()

  const {
    set: selectedDepartmentIds,
    add: selectDepartment,
    remove: unselectDepartment,
    clear: clearSelectedDepartmentIds,
  } = useSet<string>()

  const {
    set: selectedUserIds,
    add: selectUser,
    remove: removeUser,
  } = useSet<number>()

  const [allFacilities, setAllFacilities] = useState(true)
  const [allDepartments, setAllDepartments] = useState(true)

  const createApproversMutation = useCreateApprovalDomainApprovers()

  const save = async () => {
    if (selectedUserIds.size === 0) return

    try {
      await createApproversMutation.mutateAsync({
        approvalDomainGlobalId: approvalDomainId,
        userIds: Array.from(selectedUserIds),
        allFacilities,
        allWorkdayDepartments: allDepartments,
        facilityIds: allFacilities ? [] : Array.from(selectedFacilityIds),
        workdayDepartmentIds: allDepartments
          ? []
          : Array.from(selectedDepartmentIds),
      })

      onClose()
      onSuccess()
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          approvalDomainId,
        },
      })
      onError()
    }
  }

  const isValid =
    selectedUserIds.size > 0 &&
    (allFacilities || selectedFacilityIds.size > 0) &&
    (allDepartments || selectedDepartmentIds.size > 0)

  return (
    <Modal
      closeOnBackgroundClick={false}
      hidden={false}
      onClose={onClose}
      title="Add Approver"
      variant="custom"
      content={
        <ApproverModalContent
          disableSaveButton={!isValid}
          userSection={
            <UserSelection
              includeSelf={true}
              label="Select Users"
              onEnter={(user) => selectUser(user.id)}
              placeholder="Search for users..."
              renderSearchItem={(user) => (
                <EntityItem
                  key={user.id}
                  primaryText={user.name}
                  secondaryText={user.email}
                  disabled={
                    userIdsAlreadyAsApprover.has(user.id) ||
                    selectedUserIds.has(user.id)
                  }
                  onClick={() => {
                    selectUser(user.id)
                  }}
                />
              )}
              renderSelectedItem={(user) => (
                <EntityItem
                  key={user.id}
                  className="bg-neutral-100 p-3 rounded-lg hover:border-transparent"
                  primaryText={user.name}
                  secondaryText={user.email}
                  onClick={() => removeUser(user.id)}
                  removable={true}
                />
              )}
              selectedUserIds={selectedUserIds}
            />
          }
          selectedFacilityIds={selectedFacilityIds}
          selectedDepartmentIds={selectedDepartmentIds}
          allFacilities={allFacilities}
          allDepartments={allDepartments}
          onFacilitySelect={(facility, selected) => {
            const op = selected ? selectFacility : unselectFacility
            op(facility.id)
          }}
          onDepartmentSelect={(department, selected) => {
            const op = selected ? selectDepartment : unselectDepartment
            op(department.id)
          }}
          onAllFacilitiesToggle={(selected) => {
            if (selected) clearSelectedFacilityIds()
            setAllFacilities(selected)
          }}
          onAllDepartmentsToggle={(selected) => {
            if (selected) clearSelectedDepartmentIds()
            setAllDepartments(selected)
          }}
          onCancel={onClose}
          onSave={() => void save()}
          isLoading={createApproversMutation.isLoading}
          saveButtonText="Add Approvers"
        />
      }
    />
  )
}
